import { useState, useCallback, useEffect } from 'react';
import { AccountInfo, InteractionRequiredAuthError } from '@azure/msal-browser';
import MSALSingleton from '../utils/msalInstance';
import { loginRequest } from '../config/authConfig';

interface EmailLoginPayload {
  email: string;
}

interface AuthState {
  account: AccountInfo | null;
  loading: boolean;
  error: string | null;
  initialized: boolean;
}

export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    account: null,
    loading: true,
    error: null,
    initialized: false
  });

  useEffect(() => {
    const handleLogin = (event: CustomEvent<AccountInfo>) => {
      setAuthState(prev => ({
        ...prev,
        account: event.detail,
        loading: false,
        error: null
      }));
    };

    const handleLogout = () => {
      setAuthState(prev => ({
        ...prev,
        account: null,
        loading: false,
        error: null
      }));
    };

    // Add event listeners
    window.addEventListener('msalLogin', handleLogin as EventListener);
    window.addEventListener('msalLogout', handleLogout);

    // Initial auth state check
    const initializeAuth = async () => {
      try {
        await MSALSingleton.initialize();
        const msalInstance = MSALSingleton.getInstance();

        // Check localStorage first
        const savedAccount = localStorage.getItem('msal.account');
        let currentAccount: AccountInfo | null = null;

        if (savedAccount) {
          try {
            currentAccount = JSON.parse(savedAccount);
            // Verify account with MSAL
            const accounts = msalInstance.getAllAccounts();
            if (!accounts.some(a => a.homeAccountId === currentAccount!.homeAccountId)) {
              currentAccount = null;
              localStorage.removeItem('msal.account');
            }
          } catch (e) {
            localStorage.removeItem('msal.account');
          }
        }

        // If no saved account, check MSAL accounts
        if (!currentAccount) {
          const accounts = msalInstance.getAllAccounts();
          currentAccount = accounts.length > 0 ? accounts[0] : null;
          if (currentAccount) {
            localStorage.setItem('msal.account', JSON.stringify(currentAccount));
          }
        }

        // If we have an account, verify the token
        if (currentAccount) {
          try {
            await msalInstance.acquireTokenSilent({
              ...loginRequest,
              account: currentAccount
            });
          } catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
              localStorage.removeItem('msal.account');
              currentAccount = null;
            }
          }
        }

        setAuthState({
          account: currentAccount,
          loading: false,
          error: null,
          initialized: true
        });
      } catch (error) {
        console.error('Auth initialization error:', error);
        setAuthState({
          account: null,
          loading: false,
          error: 'Failed to initialize authentication',
          initialized: false
        });
      }
    };

    initializeAuth();

    // Cleanup
    return () => {
      window.removeEventListener('msalLogin', handleLogin as EventListener);
      window.removeEventListener('msalLogout', handleLogout);
    };
  }, []);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        await MSALSingleton.initialize();
        const msalInstance = MSALSingleton.getInstance();

        // Try to get account from MSAL storage
        const accounts = msalInstance.getAllAccounts();
        const currentAccount = accounts.length > 0 ? accounts[0] : null;

        // If we have an account in storage but no active tokens, try to silently acquire a new token
        if (currentAccount) {
          try {
            await msalInstance.acquireTokenSilent({
              ...loginRequest,
              account: currentAccount
            });
          } catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
              // Token expired and refresh failed - user needs to login again
              console.log('Token expired, clearing account state');
              MSALSingleton.clearAllStorage();
              setAuthState({
                account: null,
                loading: false,
                error: null,
                initialized: true
              });
              return;
            }
          }
        }

        setAuthState({
          account: currentAccount,
          loading: false,
          error: null,
          initialized: true
        });
      } catch (error) {
        console.error('Auth initialization error:', error);
        setAuthState(prev => ({
          ...prev,
          loading: false,
          error: 'Failed to initialize authentication',
          initialized: false
        }));
      }
    };

    initializeAuth();
  }, []);

  // Add an effect to handle storage events
  useEffect(() => {
    const handleStorageChange = () => {
      const msalInstance = MSALSingleton.getInstance();
      const accounts = msalInstance.getAllAccounts();
      setAuthState(prev => ({
        ...prev,
        account: accounts.length > 0 ? accounts[0] : null
      }));
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const loginWithEmail = useCallback(async ({ email }: EmailLoginPayload) => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }));

      const apiUrlRegister = process.env.REACT_APP_API_URL_REGISTER;
      if (!apiUrlRegister) {
        throw new Error('API URL for email login is not defined');
      }
      const response = await fetch(apiUrlRegister, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        throw new Error('Email login request failed');
      }

      const data = await response.json();

      // Create a simplified account object
      setAuthState({
        account: {
          homeAccountId: email,
          environment: 'email',
          tenantId: '',
          username: email,
          localAccountId: email,
          name: email,
        },
        loading: false,
        error: null,
        initialized: true
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Email login failed';
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }));
      throw error;
    }
  }, []);

  const loginWithProvider = useCallback(async (provider: 'google' | 'microsoft') => {
    if (!MSALSingleton.isInitialized()) {
      throw new Error('Authentication not initialized');
    }

    try {
      await MSALSingleton.checkAndWaitForInteraction();

      setAuthState(prev => ({ ...prev, loading: true, error: null }));
      const msalInstance = MSALSingleton.getInstance();

      // Check if there's already an account logged in
      const currentAccounts = msalInstance.getAllAccounts();
      if (currentAccounts.length > 0) {
        await msalInstance.logout();
      }

      MSALSingleton.clearAllStorage();
      MSALSingleton.setInteractionInProgress(true);

      const request = {
        ...loginRequest,
        prompt: 'select_account',
        loginHint: provider === 'google' ? '@gmail.com' : undefined
      };

      const response = await msalInstance.loginPopup(request);

      setAuthState({
        account: response.account,
        loading: false,
        error: null,
        initialized: true
      });

      return response;
    } catch (error) {
      MSALSingleton.setInteractionInProgress(false);

      if (error instanceof Error) {
        if (error.message.includes('user_cancelled') ||
          error.message.includes('popup_window_error')) {
          setAuthState(prev => ({
            ...prev,
            loading: false
          }));
          return;
        }

        if (error.message.includes('interaction_in_progress')) {
          setAuthState(prev => ({
            ...prev,
            loading: false,
            error: 'Another sign-in attempt is in progress. Please wait a moment and try again.'
          }));
          return;
        }
      }

      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }));
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    if (!MSALSingleton.isInitialized()) {
      throw new Error('Authentication not initialized');
    }

    try {
      const msalInstance = MSALSingleton.getInstance();
      const accounts = msalInstance.getAllAccounts();

      // First, try to logout from MSAL
      if (accounts.length > 0) {
        await msalInstance.logoutRedirect();
      }

      // Clear all MSAL storage
      await MSALSingleton.clearAllStorage();

      // Update auth state
      setAuthState({
        account: null,
        loading: false,
        error: null,
        initialized: true
      });

      // Force a UI refresh
      window.dispatchEvent(new Event('storage'));

      console.log('Logout completed successfully');
    } catch (error) {
      console.error('Logout error:', error);
      // Clear storage anyway
      await MSALSingleton.clearAllStorage();

      const errorMessage = error instanceof Error ? error.message : 'Logout failed';
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }));
    }
  }, []);

  const getToken = useCallback(async () => {
    if (!MSALSingleton.isInitialized()) {
      throw new Error('Authentication not initialized');
    }

    try {
      const msalInstance = MSALSingleton.getInstance();
      const account = msalInstance.getAllAccounts()[0];
      if (!account) throw new Error('No account found');

      try {
        const response = await msalInstance.acquireTokenSilent({
          ...loginRequest,
          account
        });
        return response.accessToken;
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          const response = await msalInstance.acquireTokenPopup({
            ...loginRequest,
            account
          });
          return response.accessToken;
        }
        throw error;
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to acquire token';
      setAuthState(prev => ({
        ...prev,
        error: errorMessage
      }));
      throw error;
    }
  }, []);

  return {
    ...authState,
    loginWithProvider,
    loginWithEmail,
    logout,
    getToken,
  };
};

export default useAuth;